/** One could-buy draft and its Send intent, preserved across authentication. */
const KEY = 'jaakko_pending_draft_chat';
const TTL_MS = 15 * 60 * 1000;
// Legacy bundles used an array of savedAt marks. Each new intent has its own
// outcome, so concurrent cancellations cannot overwrite another tab's claim.
const SENT_KEY = 'jaakko_pending_draft_chat_sent';
const STATE_PREFIX = `${SENT_KEY}:`;
let shownSendId: string | null = null;

export type PendingDraftChat = {
  sellWillingnessId: string;
  title: string;
  text: string;
};
type StoredDraft = PendingDraftChat & { savedAt: number; sendId?: string };
type SendOutcome = {
  status: 'claimed' | 'cancelled' | 'sent';
  expiresAt: number;
};
export type PendingDraftSendClaim = { id: string; text: string };

const sendIdFor = (draft: StoredDraft): string =>
  draft.sendId || String(draft.savedAt);

const readDraft = (): StoredDraft | null => {
  const draft = JSON.parse(sessionStorage.getItem(KEY) || 'null');
  const age = Date.now() - draft?.savedAt;
  if (
    typeof draft?.sellWillingnessId !== 'string' ||
    typeof draft?.text !== 'string' ||
    typeof draft?.savedAt !== 'number' ||
    (draft.sendId !== undefined && (typeof draft.sendId !== 'string' || !draft.sendId)) ||
    !Number.isFinite(age) ||
    age < 0 ||
    age > TTL_MS
  )
    return null;
  return draft;
};

const readOutcome = (id: string): SendOutcome | null => {
  const outcome = JSON.parse(
    localStorage.getItem(`${STATE_PREFIX}${id}`) || 'null',
  );
  if (!outcome) return null;
  if (
    !['claimed', 'cancelled', 'sent'].includes(outcome.status) ||
    typeof outcome.expiresAt !== 'number' ||
    !Number.isFinite(outcome.expiresAt)
  ) {
    throw new Error('Invalid pending Send outcome');
  }
  return outcome.expiresAt < Date.now() ? null : outcome;
};
const writeOutcome = (id: string, outcome: SendOutcome): void => {
  localStorage.setItem(`${STATE_PREFIX}${id}`, JSON.stringify(outcome));
};

/** A busy tab must not evict another live intent's claim. */
const pruneExpiredOutcomes = (): void => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(STATE_PREFIX)) continue;
    try {
      const outcome = JSON.parse(localStorage.getItem(key) || 'null');
      if (
        typeof outcome?.expiresAt === 'number' &&
        outcome.expiresAt < Date.now()
      )
        localStorage.removeItem(key);
    } catch {
      // An unreadable outcome must not become an available Send.
    }
  }
};

/** A fresh intent also needs a fresh ID when presses share a millisecond. */
export const savePendingDraftChat = (draft: PendingDraftChat): void => {
  try {
    const sendId =
      crypto.randomUUID?.() ||
      Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-');
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...draft, savedAt: Date.now(), sendId }),
    );
    shownSendId = sendId;
    try {
      pruneExpiredOutcomes();
    } catch {
      /* Shared storage may be blocked. */
    }
  } catch {
    // Storage failures must not prevent opening authentication.
  }
};

/** Retire only an intent this document saved or actually restored. */
export const clearPendingDraftChat = (): void => {
  try {
    const draft = readDraft();
    sessionStorage.removeItem(KEY);
    if (!draft || sendIdFor(draft) !== shownSendId) return;
    const id = sendIdFor(draft);
    if (readOutcome(id)?.status !== 'sent')
      writeOutcome(id, {
        status: 'cancelled',
        expiresAt: draft.savedAt + TTL_MS,
      });
  } catch {
    // A stale record expires even when shared storage cannot be updated.
  }
};

/**
 * Claim one automatic attempt under an origin-wide exclusive browser lock.
 * Without Web Locks or writable shared storage, keep the text for manual Send.
 * Re-read inside the lock: cancellation, expiry or replacement can happen while
 * waiting. A claim is an attempt, never proof that the server accepted it.
 */
export const claimPendingDraftSend = async (
  sellWillingnessId: string,
): Promise<PendingDraftSendClaim | null> => {
  try {
    const draft = readDraft();
    if (
      !draft ||
      draft.sellWillingnessId !== sellWillingnessId ||
      !draft.text.trim() ||
      !navigator.locks?.request
    )
      return null;
    const id = sendIdFor(draft);
    return await navigator.locks.request(`${STATE_PREFIX}${id}`, () => {
      const current = readDraft();
      if (
        !current ||
        sendIdFor(current) !== id ||
        current.sellWillingnessId !== sellWillingnessId ||
        readOutcome(id)
      )
        return null;
      if (!current.sendId) {
        const marks = JSON.parse(localStorage.getItem(SENT_KEY) || '[]');
        if (!Array.isArray(marks) || marks.includes(String(current.savedAt)))
          return null;
      }
      writeOutcome(id, {
        status: 'claimed',
        expiresAt: current.savedAt + TTL_MS,
      });
      return { id, text: current.text };
    });
  } catch {
    return null;
  }
};

/** Capture before a request so its response cannot confirm a newer intent. */
export const pendingDraftSendId = (
  sellWillingnessId: string,
  text: string,
): string | null => {
  try {
    const draft = readDraft();
    return draft?.sellWillingnessId === sellWillingnessId &&
      draft.text.trim() === text.trim()
      ? sendIdFor(draft)
      : null;
  } catch {
    return null;
  }
};

/** Called only after a successful send response has been parsed. */
export const confirmPendingDraftSend = (id: string | null): void => {
  if (!id) return;
  try {
    const outcome = readOutcome(id);
    if (outcome) writeOutcome(id, { ...outcome, status: 'sent' });
  } catch {
    // Failure to store confirmation must preserve text, not discard it.
  }
};

/** Matching an old message is insufficient; require a recorded successful send. */
export const pendingDraftSendDelivered = (
  sellWillingnessId: string,
  text: string,
): boolean => {
  try {
    const id = pendingDraftSendId(sellWillingnessId, text);
    return !!id && readOutcome(id)?.status === 'sent';
  } catch {
    return false;
  }
};

/** A signed-out fresh visit discards the draft; reload/Back may restore it. */
export const pendingDraftChatToRestore = (
  isLoggedIn: boolean,
): PendingDraftChat | null => {
  try {
    const entry = performance.getEntriesByType?.('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (
      !isLoggedIn &&
      entry?.type !== 'reload' &&
      entry?.type !== 'back_forward'
    ) {
      clearPendingDraftChat();
      return null;
    }
    const draft = readDraft();
    if (!draft) {
      clearPendingDraftChat();
      return null;
    }
    shownSendId = sendIdFor(draft);
    return {
      sellWillingnessId: draft.sellWillingnessId,
      title: typeof draft.title === 'string' ? draft.title : '',
      text: draft.text,
    };
  } catch {
    clearPendingDraftChat();
    return null;
  }
};
